<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-sheet outlined rounded>
      <v-tabs v-model="activeTab" background-color="grey lighten-4">
        <v-tab>
          <v-icon left small>mdi-account-group-outline</v-icon>
          Students
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-calendar-clock</v-icon>
          Sessions
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <div class="pa-4">
            <div
              class="d-flex align-center mb-4"
              style="gap: 12px; flex-wrap: wrap"
            >
              <v-select
                v-model="studentFilters.program_id"
                :items="programs"
                item-text="code"
                item-value="id"
                label="Programme"
                outlined
                dense
                hide-details="auto"
                clearable
                style="max-width: 250px"
                @change="onStudentFilterChange"
              />
              <v-select
                v-model="studentFilters.term_no"
                :items="terms"
                label="Semester"
                :loading="loading"
                outlined
                dense
                hide-details="auto"
                clearable
                :disabled="!studentFilters.program_id"
                style="max-width: 250px"
                @change="onStudentSemesterChange"
              />
              <v-select
                v-model="studentFilters.course_code"
                :items="courses"
                label="Course"
                :loading="loading"
                outlined
                dense
                hide-details="auto"
                clearable
                style="max-width: 250px"
              />
              <!-- :disabled="!studentFilters.program_id || !studentFilters.term_no" -->
              <v-text-field
                v-model="studentSearchUsn"
                label="USN"
                outlined
                dense
                hide-details="auto"
                clearable
                prepend-inner-icon="mdi-magnify"
                style="max-width: 250px"
                @keyup.enter="searchStudent"
              />
              <v-btn color="primary" @click="searchStudent" :loading="loading">
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
              <v-spacer />
              <div
                v-if="searchedStudent && studentSearchUsn"
                class="text-subtitle-2"
              >
                <span class="font-weight-medium">{{
                  searchedStudent.name
                }}</span>
                <span class="ml-2 grey--text"
                  >({{ searchedStudent.usn_id }})</span
                >
              </div>
            </div>

            <!-- Single Student View -->
            <v-data-table
              v-if="searchedStudent && !Array.isArray(searchedStudent)"
              :headers="detailedAttendanceHeaders"
              :items="studentDetailedAttendance"
              :loading="loading"
              fixed-header
              item-key="entry_id"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
              outlined
            >
              <template #[`item.webinar_date`]="{ item }">
                {{ formatDate(item.webinar_date) }}
              </template>
              <template #[`item.time`]="{ item }">
                {{ formatTime(item.from_time) }} –
                {{ formatTime(item.to_time) }}
              </template>
              <template #[`item.semester_number`]="{ item }">
                {{ item.semester_number || "—" }}
              </template>
              <template #[`item.program_code`]="{ item }">
                {{ item.program_code || "—" }}
              </template>
              <template #[`item.class_id`]="{ item }">
                {{ item.class_id || "—" }}
              </template>
              <template #[`item.course_code`]="{ item }">
                {{ item.course_code || "—" }}
              </template>
              <template #[`item.faculty_name`]="{ item }">
                {{ item.faculty_name || "—" }}
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="statusChipColor(item.status)"
                  class="white--text"
                >
                  {{ statusText(item.status) }}
                </v-chip>
              </template>
              <template #no-data>
                <div class="text-center pa-6 grey--text">
                  No attendance records found
                </div>
              </template>
            </v-data-table>

            <!-- Multiple Students View - FLAT TABLE -->
            <v-data-table
              v-else-if="searchedStudent && Array.isArray(searchedStudent)"
              :headers="multipleStudentsHeaders"
              :items="multipleStudentsData"
              :loading="loading"
              fixed-header
              item-key="row_key"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
              outlined
            >
              <!-- Student Name Column -->
              <template #[`item.name`]="{ item }">
                <div>
                  <strong>{{ item.name }}</strong>
                  <div class="text-caption grey--text">{{ item.usn_id }}</div>
                </div>
              </template>

              <template #[`item.webinar_date`]="{ item }">
                {{ formatDate(item.webinar_date) }}
              </template>

              <template #[`item.time`]="{ item }">
                {{ formatTime(item.from_time) }} –
                {{ formatTime(item.to_time) }}
              </template>

              <template #[`item.semester_number`]="{ item }">
                {{ item.semester_number || "—" }}
              </template>

              <template #[`item.program_code`]="{ item }">
                {{ item.program_code || "—" }}
              </template>

              <template #[`item.class_id`]="{ item }">
                {{ item.class_id || "—" }}
              </template>

              <template #[`item.course_code`]="{ item }">
                {{ item.course_code || "—" }}
              </template>

              <template #[`item.faculty_name`]="{ item }">
                {{ item.faculty_name || "—" }}
              </template>

              <template #[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="statusChipColor(item.status)"
                  class="white--text"
                >
                  {{ statusText(item.status) }}
                </v-chip>
              </template>
              <template #no-data>
                <div class="text-center pa-6 grey--text">
                  No attendance records found
                </div>
              </template>
            </v-data-table>

            <div v-else class="text-center pa-8 grey--text">
              <v-icon size="48" class="mb-2 grey--text text--lighten-1"
                >mdi-magnify</v-icon
              >
              <p>click search to view webinar attendance</p>
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <!-- Breadcrumb navigation -->
          <div class="d-flex align-center px-4 py-2 flex-wrap">
            <v-btn
              text
              small
              :color="selectedRun ? 'primary' : 'grey darken-2'"
              class="text-none px-2"
              @click="goToRuns"
            >
              <v-icon left small>mdi-calendar-multiple</v-icon>
              Schedules
            </v-btn>
            <template v-if="selectedRun">
              <v-icon small color="grey">mdi-chevron-right</v-icon>
              <v-btn
                text
                small
                :color="selectedSession ? 'primary' : 'grey darken-2'"
                class="text-none px-2"
                @click="goToSessions"
              >
                {{ selectedRun.title }}
              </v-btn>
            </template>
            <template v-if="selectedSession">
              <v-icon small color="grey">mdi-chevron-right</v-icon>
              <span class="text-subtitle-2 font-weight-medium ml-2">
                {{ formatDate(selectedSession.webinar_date) }},
                {{ formatTime(selectedSession.from_time) }} –
                {{ formatTime(selectedSession.to_time) }}
              </span>
            </template>
          </div>

          <v-divider />

          <!-- Level 1: list of schedule runs (grouped by title) -->
          <v-data-table
            v-if="!selectedRun"
            :headers="runHeaders"
            :items="runGroups"
            :loading="loading"
            fixed-header
            item-key="group_key"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            class="row-pointer"
            @click:row="openRun"
          >
            <template #[`item.date_range`]="{ item }">
              <span v-if="item.first_date === item.last_date">
                {{ formatDate(item.first_date) }}
              </span>
              <span v-else>
                {{ formatDate(item.first_date) }} –
                {{ formatDate(item.last_date) }}
              </span>
            </template>
            <template #[`item.attendance_percentage`]="{ item }">
              {{ formatPercent(item.attendance_percentage) }}
            </template>
            <template #[`item.action`]>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </template>
            <template #no-data>
              <div class="text-center pa-6 grey--text">No schedules found</div>
            </template>
          </v-data-table>

          <!-- Level 2: list of sessions within the selected run -->
          <div
            v-else-if="!selectedSession"
            class="d-flex align-center px-4 py-3"
            style="gap: 8px; flex-wrap: wrap"
          >
            <v-chip small outlined>
              {{ selectedRunSessions.length }} session(s)
            </v-chip>
            <v-spacer />
            <v-btn
              color="accent white--text"
              :disabled="!selectedRunSessions.length"
              @click="downloadSessions"
            >
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
          </div>
          <v-data-table
            v-if="selectedRun && !selectedSession"
            :headers="sessionHeaders"
            :items="selectedRunSessions"
            :loading="loading"
            fixed-header
            item-key="id"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            class="row-pointer"
            @click:row="openSession"
          >
            <template #[`item.webinar_date`]="{ item }">
              {{ formatDate(item.webinar_date) }}
            </template>
            <template #[`item.faculty_name`]="{ item }">
              {{ item.faculty_name || "—" }}
            </template>
            <template #[`item.time`]="{ item }">
              {{ formatTime(item.from_time) }} – {{ formatTime(item.to_time) }}
            </template>
            <template #[`item.rescheduled`]="{ item }">
              <v-chip v-if="item.rescheduled" x-small color="orange lighten-4">
                Rescheduled
              </v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template #[`item.attendance_percentage`]="{ item }">
              {{ formatPercent(item.attendance_percentage) }}
            </template>
            <template #[`item.action`]>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </template>
            <template #no-data>
              <div class="text-center pa-6 grey--text">No sessions found</div>
            </template>
          </v-data-table>

          <!-- Level 3: list of students for the selected session (inline) -->
          <div v-if="selectedRun && selectedSession" class="pa-4">
            <div
              class="d-flex align-center mb-3"
              style="gap: 8px; flex-wrap: wrap"
            >
              <v-chip small color="grey darken-2" dark>
                Total: {{ selectedSession.total_students }}
              </v-chip>
              <v-chip small color="success darken-3" dark>
                Present: {{ selectedSession.present_count }}
              </v-chip>
              <v-chip small color="error darken-3" dark>
                Absent: {{ selectedSession.absent_count }}
              </v-chip>
              <v-chip small outlined>
                {{ formatPercent(selectedSession.attendance_percentage) }}
              </v-chip>

              <v-spacer />

              <v-text-field
                v-model="matrixSearch"
                label="Search by USN ID"
                outlined
                dense
                hide-details="auto"
                clearable
                prepend-inner-icon="mdi-magnify"
                style="max-width: 320px"
              />

              <v-btn
                color="accent white--text"
                :disabled="!sessionMatrixRows.length"
                @click="downloadSessionStudents"
              >
                <v-icon left>mdi-download</v-icon>
                Download
              </v-btn>
            </div>

            <v-data-table
              :headers="matrixHeaders"
              :items="sessionMatrixRows"
              :search="matrixSearch"
              :loading="loading"
              dense
              fixed-header
              item-key="usn_id"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            >
              <template #[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="statusChipColor(item.status)"
                  class="white--text"
                >
                  {{ statusText(item.status) }}
                </v-chip>
              </template>
              <template #no-data>
                <div class="text-center pa-6 grey--text">
                  No student data for this session
                </div>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-sheet>
  </v-container>
</template>

<script>
import {
  studentAttendanceHeaders,
  webinarSessionHeaders,
  matrixHeaders,
} from "@/constants/headers";
export default {
  data() {
    return {
      loading: false,
      activeTab: 0,
      usnId: null,
      studentSearchUsn: "",
      searchedStudent: null,
      report: null,
      selectedSession: null,
      selectedSessionStudents: [],
      selectedRun: null,
      matrixSearch: "",
      studentFilters: {
        program_id: null,
        term_no: null,
        course_code: null,
      },
      // Filters as of the last Search — the USN result is narrowed to these too,
      // so a USN lookup is never independent of the programme/semester/course.
      appliedStudentFilters: {
        program_id: null,
        term_no: null,
        course_code: null,
      },
      programs: [],
      terms: [],
      courses: [],
      programsLoading: false,
      termsLoading: false,
      coursesLoading: false,
      studentHeaders: studentAttendanceHeaders,
      sessionHeaders: webinarSessionHeaders,
      matrixHeaders: matrixHeaders,
      detailedAttendanceHeaders: [
        { text: "Date", value: "webinar_date" },
        { text: "Time", value: "time" },
        { text: "Semester", value: "semester_number" },
        { text: "Programme", value: "program_code" },
        { text: "Class ID", value: "class_id" },
        { text: "Course", value: "course_code" },
        { text: "Faculty", value: "faculty_name" },
        { text: "Status", value: "status" },
      ],
      multipleStudentsHeaders: [
        { text: "Student", value: "name" },
        { text: "Date", value: "webinar_date" },
        { text: "Time", value: "time" },
        { text: "Semester", value: "semester_number" },
        { text: "Programme", value: "program_code" },
        { text: "Class ID", value: "class_id" },
        { text: "Course", value: "course_code" },
        { text: "Faculty", value: "faculty_name" },
        { text: "Status", value: "status" },
      ],
      runHeaders: [
        { text: "Title", value: "title" },
        { text: "Sessions", value: "session_count" },
        { text: "Date Range", value: "date_range" },
        { text: "Attendance %", value: "attendance_percentage" },
        { text: "", value: "action", sortable: false, align: "end" },
      ],
    };
  },

  computed: {
    sessions() {
      return this.report?.sessions || [];
    },
    studentDetailedAttendance() {
      if (!this.searchedStudent || !this.searchedStudent.sessions) return [];

      const attendedSessions = [];
      const seenSessionIds = new Set();

      for (const sessionData of this.searchedStudent.sessions) {
        if (seenSessionIds.has(sessionData.entry_id)) continue;
        seenSessionIds.add(sessionData.entry_id);

        const session = this.sessions.find(
          (s) => s.id === sessionData.entry_id,
        );
        if (session && this.matchesStudentFilters(session)) {
          attendedSessions.push({
            entry_id: session.id,
            webinar_date: session.webinar_date,
            from_time: session.from_time,
            to_time: session.to_time,
            run_title: session.run_title,
            course_code: session.course_code,
            semester_number: session.semester_number,
            program_code: session.program_code,
            class_id: session.class_id,
            faculty_name: session.faculty_name,
            status: sessionData.status,
          });
        }
      }

      return attendedSessions.sort((a, b) =>
        b.webinar_date.localeCompare(a.webinar_date),
      );
    },

    // Sessions carry program_code, while the filter holds the programme id.
    appliedProgramCode() {
      const program = this.programs.find(
        (p) => p.id === this.appliedStudentFilters.program_id,
      );
      return program ? program.code : null;
    },

    multipleStudentsData() {
      if (!Array.isArray(this.searchedStudent)) return [];

      return this.searchedStudent.map((item, index) => ({
        ...item,
        row_key: `${item.usn_id}_${item.entry_id}_${index}`,
      }));
    },

    runGroups() {
      const groups = {};
      for (const session of this.sessions) {
        const key =
          session.scheduler_run_id != null
            ? String(session.scheduler_run_id)
            : "none";
        if (!groups[key]) {
          groups[key] = {
            group_key: key,
            scheduler_run_id: session.scheduler_run_id ?? null,
            title: session.run_title || "Untitled Schedule",
            sessions: [],
            total_students: 0,
            present_count: 0,
            first_date: session.webinar_date,
            last_date: session.webinar_date,
          };
        }
        const group = groups[key];
        group.sessions.push(session);
        group.total_students += session.total_students || 0;
        group.present_count += session.present_count || 0;
        if (session.webinar_date < group.first_date)
          group.first_date = session.webinar_date;
        if (session.webinar_date > group.last_date)
          group.last_date = session.webinar_date;
      }
      return Object.values(groups)
        .map((group) => ({
          ...group,
          session_count: group.sessions.length,
          attendance_percentage: group.total_students
            ? (group.present_count / group.total_students) * 100
            : 0,
        }))
        .sort((a, b) => {
          if (a.first_date !== b.first_date)
            return a.first_date < b.first_date ? -1 : 1;
          return a.last_date < b.last_date
            ? -1
            : a.last_date > b.last_date
            ? 1
            : 0;
        });
    },

    selectedRunSessions() {
      if (!this.selectedRun) return [];
      const key = this.selectedRun.group_key;
      return this.sessions.filter(
        (session) =>
          (session.scheduler_run_id != null
            ? String(session.scheduler_run_id)
            : "none") === key,
      );
    },

    sessionMatrixRows() {
      if (!this.selectedSession || this.selectedSessionStudents.length === 0)
        return [];
      const searchQuery = this.matrixSearch.trim();

      if (searchQuery === "") {
        return this.selectedSessionStudents;
      }

      return this.selectedSessionStudents.filter(
        (student) =>
          student.usn_id &&
          student.usn_id.toUpperCase() === searchQuery.toUpperCase(),
      );
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Webinar Attendance Report" },
    ]);
    await this.fetchPrograms();
    await this.loadReport();
  },

  methods: {
    // A USN result must respect the programme / semester / course selection.
    matchesStudentFilters(session) {
      const filters = this.appliedStudentFilters;

      if (
        filters.program_id &&
        this.appliedProgramCode &&
        session.program_code !== this.appliedProgramCode
      ) {
        return false;
      }
      if (
        filters.term_no &&
        Number(session.semester_number) !== Number(filters.term_no)
      ) {
        return false;
      }
      if (filters.course_code && session.course_code !== filters.course_code) {
        return false;
      }
      return true;
    },

    async searchStudent() {
      const hasUSN = (this.studentSearchUsn || "").trim();
      const hasFilters =
        this.studentFilters.program_id ||
        this.studentFilters.term_no ||
        this.studentFilters.course_code;

      if (!hasUSN && !hasFilters) {
        this.$store.commit("showSnackbar", {
          message: "Please enter a USN or select at least one filter",
          color: "warning",
        });
        this.searchedStudent = null;
        return;
      }

      this.loading = true;
      this.appliedStudentFilters = { ...this.studentFilters };
      try {
        const params = { ...this.studentFilters };
        const queryString = this.objectToQueryParams(params);

        let endpoint;
        if (hasUSN) {
          const usn = encodeURIComponent(this.studentSearchUsn.trim());
          endpoint = `/scheduler/attendance_report/student/${usn}?${queryString}`;
        } else {
          endpoint = `/scheduler/attendance_report/students?${queryString}`;
        }

        const res = await this.$axios.get(endpoint);
        this.searchedStudent = res.data.data || null;
        if (
          !this.searchedStudent ||
          (Array.isArray(this.searchedStudent) &&
            this.searchedStudent.length === 0)
        ) {
          this.$store.commit("showSnackbar", {
            message: "No students found",
            color: "error",
          });
        }
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "No students found";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
        this.searchedStudent = null;
      } finally {
        this.loading = false;
      }
    },

    async loadReport() {
      this.loading = true;
      this.report = null;
      this.selectedRun = null;
      this.selectedSession = null;
      try {
        const queryString = this.objectToQueryParams({ ...this.form });
        const res = await this.$axios.get(
          `/scheduler/attendance_report?${queryString}`,
        );
        this.report = res.data.data || null;
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Unable to load attendance report";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.loading = false;
      }
    },

    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }

      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },

    async fetchPrograms() {
      this.programsLoading = true;
      try {
        const res = await this.$axios.get("/webinar/programs");
        this.programs = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.programsLoading = false;
      }
    },

    async fetchTerms() {
      if (!this.form.program_id) {
        this.terms = [];
        return;
      }
      this.termsLoading = true;
      try {
        const res = await this.$axios.get(
          `/webinar/terms?program_id=${this.form.program_id}`,
        );
        this.terms = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.termsLoading = false;
      }
    },

    async fetchCourses() {
      if (!this.form.program_id || !this.form.term_no) {
        this.courses = [];
        return;
      }
      this.coursesLoading = true;
      try {
        const res = await this.$axios.get(
          `/webinar/courses?program_id=${this.form.program_id}&term_no=${this.form.term_no}`,
        );
        this.courses = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.coursesLoading = false;
      }
    },

    onStudentFilterChange() {
      if (this.studentFilters.program_id) {
        this.studentFilters.term_no = null;
        this.studentFilters.course_code = null;
        this.terms = [];
        this.fetchStudentTerms();
        this.fetchAllCourses();
      } else {
        this.studentFilters.term_no = null;
        this.studentFilters.course_code = null;
        this.terms = [];
        this.fetchAllCourses();
      }
    },

    onStudentSemesterChange() {
      this.studentFilters.course_code = null;
      this.fetchAllCourses();
    },

    async fetchStudentTerms() {
      if (!this.studentFilters.program_id) {
        this.terms = [];
        return;
      }
      this.termsLoading = true;
      try {
        const res = await this.$axios.get(
          `/webinar/terms?program_id=${this.studentFilters.program_id}`,
        );
        this.terms = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.termsLoading = false;
      }
    },

    async fetchAllCourses() {
      if (this.studentFilters.program_id && this.studentFilters.term_no) {
        // Fetch courses for specific program and semester
        this.coursesLoading = true;
        try {
          const res = await this.$axios.get(
            `/webinar/courses?program_id=${this.studentFilters.program_id}&term_no=${this.studentFilters.term_no}`,
          );
          this.courses = res.data.data || [];
        } catch (e) {
          console.log(e);
        } finally {
          this.coursesLoading = false;
        }
      } else if (this.studentFilters.program_id) {
        // Fetch all courses for the program
        this.coursesLoading = true;
        try {
          const res = await this.$axios.get(
            `/webinar/courses?program_id=${this.studentFilters.program_id}`,
          );
          this.courses = res.data.data || [];
        } catch (e) {
          console.log(e);
        } finally {
          this.coursesLoading = false;
        }
      } else {
        this.courses = [];
      }
    },

    onProgramChange() {
      this.form.term_no = null;
      this.form.course_code = null;
      this.terms = [];
      this.courses = [];
      this.fetchTerms();
    },

    onTermChange() {
      this.form.course_code = null;
      this.courses = [];
      this.fetchCourses();
    },

    openRun(run) {
      this.selectedRun = run;
      this.selectedSession = null;
    },

    async openSession(session) {
      this.selectedSession = session;
      this.matrixSearch = "";
      this.selectedSessionStudents = [];

      this.loading = true;
      try {
        const queryString = this.objectToQueryParams({ ...this.form });
        const res = await this.$axios.get(
          `/scheduler/attendance_report/session/${session.id}?${queryString}`,
        );
        const data = res.data.data || {};
        this.selectedSessionStudents = data.students || [];
      } catch (e) {
        const msg =
          e.response?.data?.error || "Failed to load session students";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
        this.selectedSessionStudents = [];
      } finally {
        this.loading = false;
      }
    },

    goToRuns() {
      this.selectedRun = null;
      this.selectedSession = null;
      this.selectedSessionStudents = [];
    },

    goToSessions() {
      this.selectedSession = null;
      this.selectedSessionStudents = [];
    },

    flattenMultipleStudents(students) {
      if (!Array.isArray(students)) return [];

      return students.flatMap((student) =>
        (student.sessions || []).map((session, sessionIndex) => ({
          ...session,
          name: student.name,
          usn_id: student.usn_id,
          row_key: `${student.usn_id}_${sessionIndex}`,
        })),
      );
    },

    statusText(status) {
      return (
        { present: "Present", absent: "Absent", not_uploaded: "Not uploaded" }[
          status
        ] || "—"
      );
    },

    statusChipColor(status) {
      return (
        {
          present: "success darken-4",
          absent: "error darken-4",
          not_uploaded: "grey darken-2",
        }[status] || "grey darken-2"
      );
    },

    getAttendanceStats(sessions) {
      if (!sessions || sessions.length === 0) return "No sessions";

      const present = sessions.filter((s) => s.status === "present").length;
      const absent = sessions.filter((s) => s.status === "absent").length;
      const notUploaded = sessions.filter(
        (s) => s.status === "not_uploaded",
      ).length;

      return `P: ${present} | A: ${absent} | U: ${notUploaded}`;
    },

    getAttendanceColor(sessions) {
      if (!sessions || sessions.length === 0) return "grey lighten-1";

      const present = sessions.filter((s) => s.status === "present").length;
      const total = sessions.length;
      const percentage = (present / total) * 100;

      if (percentage >= 80) return "success darken-2";
      if (percentage >= 50) return "warning darken-2";
      return "error darken-2";
    },

    downloadReport() {
      this.$store.commit("showSnackbar", {
        message:
          "Download feature requires fetching all students. This will be optimized in a future update.",
        color: "info",
      });
    },

    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },

    sessionStatus(student, entryId) {
      return (
        student.sessions?.find((s) => s.entry_id === entryId)?.status ||
        "not_uploaded"
      );
    },

    sessionLabel(session) {
      return `${this.formatDate(session.webinar_date)} ${this.formatTime(
        session.from_time,
      )}`;
    },

    formatDate(d) {
      if (!d) return "-";
      const [y, m, dd] = String(d).split("-");
      return `${dd}-${m}-${y}`;
    },

    formatTime(t) {
      if (!t) return "-";
      return String(t).split(":").slice(0, 2).join(":");
    },

    formatPercent(v) {
      return `${Number(v || 0).toFixed(2)}%`;
    },

    csvValue(v) {
      const t = v === null || v === undefined ? "" : String(v);
      return `"${t.replace(/"/g, '""')}"`;
    },

    // Same client-side CSV download the schedule entries screen uses.
    downloadCsv(headers, rows, fileName) {
      const csv = [
        headers.join(","),
        ...rows.map((row) => row.map(this.csvValue).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },

    safeFileName(value) {
      return String(value || "")
        .trim()
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_+|_+$/g, "");
    },

    // Sessions listed under the selected schedule.
    downloadSessions() {
      const sessions = this.selectedRunSessions;
      if (!sessions.length) return;

      const headers = [
        "program_code",
        "semester_number",
        "class_id",
        "course_code",
        "faculty_name",
        "webinar_date",
        "slot",
        "from_time",
        "to_time",
        "rescheduled",
        "total_students",
        "uploaded_count",
        "present_count",
        "absent_count",
        "not_uploaded_count",
        "attendance_percentage",
      ];

      const rows = sessions.map((session) => [
        session.program_code,
        session.semester_number,
        session.class_id,
        session.course_code,
        session.faculty_name,
        session.webinar_date,
        session.slot,
        this.formatTime(session.from_time),
        this.formatTime(session.to_time),
        session.rescheduled ? "Yes" : "No",
        session.total_students,
        session.uploaded_count,
        session.present_count,
        session.absent_count,
        session.not_uploaded_count,
        this.formatPercent(session.attendance_percentage),
      ]);

      const title = this.safeFileName(this.selectedRun?.title) || "schedule";
      this.downloadCsv(headers, rows, `webinar_sessions_${title}.csv`);
    },

    // Students of the selected session, honouring the USN search box.
    downloadSessionStudents() {
      const students = this.sessionMatrixRows;
      if (!students.length) return;

      const headers = [
        "usn_id",
        "name",
        "email",
        "status",
        "program_code",
        "semester_number",
        "class_id",
        "course_code",
        "faculty_name",
        "webinar_date",
        "from_time",
        "to_time",
      ];

      const session = this.selectedSession || {};
      const rows = students.map((student) => [
        student.usn_id,
        student.name,
        student.email,
        this.statusText(student.status),
        session.program_code,
        session.semester_number,
        session.class_id,
        session.course_code,
        session.faculty_name,
        session.webinar_date,
        this.formatTime(session.from_time),
        this.formatTime(session.to_time),
      ]);

      const course = this.safeFileName(session.course_code);
      const date = this.safeFileName(session.webinar_date);
      const suffix = [course, date].filter(Boolean).join("_") || "session";
      this.downloadCsv(headers, rows, `webinar_attendance_${suffix}.csv`);
    },
  },
};
</script>

<style scoped>
.row-pointer ::v-deep tbody tr {
  cursor: pointer;
}
.row-pointer ::v-deep tbody tr:hover td {
  background: #f5f5f5;
}

.rounded-lg {
  border-radius: 12px;
}
.primary-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #2196f3 100%);
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15);
}
</style>
